import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import validator from 'validator';
import { sendLead } from '../lib/emailLead';
import { trackMeta } from '../lib/metaTrack';

/* -------------------------------------------------------------------------
   HeroLeadForm — short, single-line lead capture under the VSL on /erstanalyse.
   Name · E-Mail · Telefon · Interesse + submit, with an implicit privacy line
   (no checkbox, to reduce friction).

   Validation is real: `validator.isEmail` for the address and libphonenumber-js
   `isValidPhoneNumber` for the number (the international phone widget yields an
   E.164 string it can validate against the chosen country).

   NOT wired: a valid submit only logs the payload. Hook to routers/forms.py
   once approved.
   ------------------------------------------------------------------------- */

const INTERESTS = ['Mietverwaltung (ab 1 Objekt)', 'Portfoliobetreuung (ab 3 Objekten)'];

export default function HeroLeadForm() {
  const [data, setData] = useState({ name: '', email: '', phone: '', interest: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = 'Bitte ausfüllen.';
    if (!validator.isEmail(data.email.trim())) e.email = 'Gültige E-Mail erforderlich.';
    if (!data.phone || !isValidPhoneNumber(data.phone)) e.phone = 'Gültige Telefonnummer erforderlich.';
    if (!data.interest) e.interest = 'Bitte wählen.';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    setStatus('sending');
    try {
      await sendLead({
        formType: 'Kurzanfrage (Erstanalyse)',
        replyTo: data.email.trim(),
        fields: {
          Name: data.name.trim(),
          'E-Mail': data.email.trim(),
          Telefon: data.phone,
          Interesse: data.interest,
        },
      });
      trackMeta('CompleteRegistration', {
        userData: { email: data.email.trim(), phone: data.phone },
        customData: { content_name: 'Kurzanfrage' },
      });
      setStatus('sent');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[HeroLeadForm] send failed:', err);
      setStatus('error');
    }
  };

  if (status === 'sent') {
    // No `reveal` class here: the scroll-observer only tags elements present at
    // mount, so a post-submit `.reveal` would stay at opacity:0 (blank).
    return (
      <div className="hero-leadform" style={{ textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--anthracite)', margin: 0 }}>
          Vielen Dank — Ihre Anfrage ist bei uns eingegangen.
        </p>
        <p className="muted" style={{ fontSize: 14, marginTop: 10 }}>
          Wir melden uns zeitnah bei Ihnen.
        </p>
      </div>
    );
  }

  return (
    <form className="hero-leadform reveal" onSubmit={handleSubmit} noValidate>
      <div className="hero-leadform__row">
        <div className={`field ${errors.name ? 'error' : ''}`}>
          <label className="field-label" htmlFor="hlf-name">Name</label>
          <input id="hlf-name" type="text" value={data.name}
            onChange={(e) => set('name')(e.target.value)} autoComplete="name" />
          {errors.name && <div className="field-error">{errors.name}</div>}
        </div>

        <div className={`field ${errors.email ? 'error' : ''}`}>
          <label className="field-label" htmlFor="hlf-email">E-Mail</label>
          <input id="hlf-email" type="email" value={data.email}
            onChange={(e) => set('email')(e.target.value)} autoComplete="email" />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>

        <div className={`field phone-field ${errors.phone ? 'error' : ''}`}>
          <label className="field-label">Telefon</label>
          <PhoneInput defaultCountry="de" value={data.phone}
            onChange={(phone) => set('phone')(phone)}
            inputProps={{ 'aria-label': 'Telefon', autoComplete: 'tel' }} />
          {errors.phone && <div className="field-error">{errors.phone}</div>}
        </div>

        <div className={`field ${errors.interest ? 'error' : ''}`}>
          <label className="field-label" htmlFor="hlf-interest">Ich interessiere mich für</label>
          <select id="hlf-interest" value={data.interest} onChange={(e) => set('interest')(e.target.value)}>
            <option value="">— Bitte wählen</option>
            {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {errors.interest && <div className="field-error">{errors.interest}</div>}
        </div>

        <button type="submit" className="hero-leadform__submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Wird gesendet…' : <>Anfragen <span aria-hidden="true">→</span></>}
        </button>
      </div>

      {status === 'error' && (
        <p role="alert" style={{ marginTop: 14, color: 'var(--bordeaux)', fontSize: 13 }}>
          Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie an{' '}
          <a href="mailto:frank.balz@hambacore.com" style={{ color: 'var(--bordeaux)' }}>frank.balz@hambacore.com</a>.
        </p>
      )}

      <p className="hero-leadform__privacy">
        Mit dem Absenden bestätigen Sie, dass Sie unsere{' '}
        <Link to="/agb" target="_blank" rel="noopener noreferrer">AGB</Link>
        {' '}und{' '}
        <Link to="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzhinweise</Link>
        {' '}gelesen haben und der Verarbeitung Ihrer Angaben zur Bearbeitung Ihrer Anfrage zustimmen.
      </p>
    </form>
  );
}
